import { RegisterResponse } from '@/happypaws-shared/src/Authentication/models/RegisterResponse';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useAuth } from 'context/AuthContext';
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { UserRole } from 'shared/constants';

/**
 * Handles the redirect back from NestJS after Google OAuth.
 *
 * NestJS redirects here with query params:
 *   ?accessToken=xxx&id=xxx&email=xxx&firstName=xxx&lastName=xxx&activeRole=xxx&roles=xxx
 *
 * This page mirrors what authService.login() does, then navigates to the right dashboard.
 * Uses declarative <Navigate> instead of imperative navigate() to avoid the race condition
 * where ClientGuard renders before AuthContext commits the new user state.
 */
const AuthCallback: React.FC = () => {
  const { setUser } = useAuth();
  const [parsedUser, setParsedUser] = useState<RegisterResponse | null>(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const accessToken = params.get('accessToken');
    const id = params.get('id');
    const email = params.get('email');
    const firstName = params.get('firstName') ?? '';
    const lastName = params.get('lastName') ?? '';
    const activeRole = params.get('activeRole') as UserRole;
    const rolesParam = params.get('roles');
    const roles = rolesParam ? (rolesParam.split(',') as UserRole[]) : [activeRole];

    if (!accessToken || !id || !email || !activeRole) {
      setHasError(true);
      return;
    }

    const user: RegisterResponse = {
      id,
      email,
      firstName,
      lastName,
      accessToken,
      activeRole,
      roles,
    };

    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
    setParsedUser(user);
  }, []);

  if (hasError) return <Navigate to="/login" replace />;
  if (parsedUser) {
    return <Navigate to={parsedUser.activeRole === UserRole.SITTER ? '/sitter-dashboard' : '/dashboard'} replace />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: 2,
        backgroundColor: '#FAF8F5',
      }}
    >
      <CircularProgress sx={{ color: '#178B8E' }} />
      <Typography variant="body1" sx={{ color: '#5A5A5A' }}>
        Signing you in...
      </Typography>
    </Box>
  );
};

export default AuthCallback;
